import { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Avatar, Button, Divider, Skeleton, Paper,
} from '@mui/material';
import { PersonRemove } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import userService, { UserSummary } from '../../services/userService';
import { colors } from '../../theme/theme';

export default function ConnectionsPage() {
  const { t } = useTranslation();
  const [connections, setConnections] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const data = await userService.getConnections();
      setConnections(data);
    } catch (err) {
      // Empty state is shown to user; log for observability.
      console.warn('[ConnectionsPage] load connections failed', err);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleRemove = async (userId: string) => {
    await userService.removeConnection(userId);
    load();
  };

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>{t('pages.connections.title')}</Typography>

      {loading ? (
        [1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={70} sx={{ mb: 2, borderRadius: 3 }} />)
      ) : connections.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h1" sx={{ fontSize: '3rem', mb: 1 }}>🤝</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>No connections yet</Typography>
          <Typography color="text.secondary">Attend events and connect with your tablemates!</Typography>
        </Box>
      ) : (
        <Paper>
          {connections.map((c, i) => (
            <Box key={c.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                <Avatar src={c.avatar_url || undefined} sx={{ width: 48, height: 48, bgcolor: colors.primary.light }}>
                  {c.first_name[0]}{c.last_name[0]}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{c.first_name} {c.last_name}</Typography>
                  {c.bio && <Typography variant="body2" color="text.secondary">{c.bio}</Typography>}
                </Box>
                {c.industry && <Typography variant="caption" sx={{ color: colors.neutral[500] }}>{c.industry}</Typography>}
                <Button size="small" color="error" startIcon={<PersonRemove />} onClick={() => handleRemove(c.id)}>
                  Remove
                </Button>
              </Box>
              {i < connections.length - 1 && <Divider />}
            </Box>
          ))}
        </Paper>
      )}
    </Container>
  );
}
